/**
 * face-retouch / exif-carry.js — дата съёмки и камера обязаны пережить ретушь.
 *
 * `canvas.toBlob('image/jpeg')` не переносит EXIF ВООБЩЕ: сохранённый снимок
 * выходит без даты съёмки, модели камеры, объектива и ориентации. Заметно это
 * не сразу — файл открывается и выглядит правильно, — а потом: галерея
 * расставляет такие снимки по дате СОХРАНЕНИЯ, и поездка рассыпается.
 *
 * Перенос делает общий примитив photo-watermark/exif.js. Здесь — единственное
 * место, где живёт знание о том, КАК его звать, и вот почему оно отдельным
 * файлом, а не тремя строками внутри тула:
 *
 *   ⚠️ injectInto(newJpegBlob, sourceBYTES, opts) ждёт БАЙТЫ исходника, а не
 *   File. Передать File не ошибка времени выполнения: sourceBytes[0] у него
 *   undefined, примитив тихо возвращает ТОТ ЖЕ blob, и «перенос» выглядит
 *   работающим, не делая ничего. Такую осечку не видно ни в консоли, ни на
 *   картинке — только в метаданных сохранённого файла, куда никто не смотрит.
 *   Поэтому вызов вынесен сюда и накрыт сторожем, который зовёт эту функцию
 *   настоящим File и читает EXIF результата
 *   (tests/playwright/face-retouch-keeps-capture-date.mjs).
 *
 * ⚠️ Метаданные переносятся ЦЕЛИКОМ, включая координаты, если они были в
 * исходнике — как и у соседнего /photo-watermark (stripGps там по умолчанию
 * false). Удалять то, о чём не просили, было бы такой же неожиданностью, как
 * и терять дату; для обратного случая есть отдельный тул очистки, и об этом
 * сказано в вопросах-ответах тула, а не умолчано.
 */
(function () {
    'use strict';

    /**
     * @param {Blob}     blob     заново закодированный JPEG
     * @param {File}     srcFile  исходный файл пользователя
     * @param {string}   exifUrl  версионированный адрес примитива
     * @param {Function} [onEvent] (name, payload) — телеметрия тула
     * @returns {Promise<Blob>} файл с метаданными либо исходный blob
     */
    async function carry(blob, srcFile, exifUrl, onEvent) {
        var say = typeof onEvent === 'function' ? onEvent : function () {};
        if (!blob || !srcFile || !exifUrl) return blob;
        // EXIF живёт только в JPEG — и в исходнике, и в ВЫДАЧЕ. Сегмент APP1,
        // впрыснутый в PNG или WebP, испортил бы файл целиком: у них другой
        // контейнер, а `injectInto` проверяет только исходник.
        if (blob.type && blob.type !== 'image/jpeg') return blob;
        if (srcFile.type !== 'image/jpeg' && !/\.jpe?g$/i.test(srcFile.name || '')) return blob;

        try {
            var bytes = new Uint8Array(await srcFile.arrayBuffer());
            var mod = await import(exifUrl);
            var api = mod && (mod.default || mod);
            if (!api || typeof api.injectInto !== 'function') {
                say('tool_diagnostic', { issue: 'exif_module_shape' });
                return blob;
            }
            // resetOrientation — не украшение. Битмап декодируется с
            // `imageOrientation:'from-image'`, то есть пиксели УЖЕ развёрнуты,
            // а теперь тул умеет ещё и поворачивать кадр руками. Унесённый из
            // исходника Orientation развернул бы снимок ВТОРОЙ раз в
            // Проводнике, «Фото» на iOS и Lightroom — при том что в браузере
            // всё выглядело бы правильно.
            var withExif = await api.injectInto(blob, bytes, { resetOrientation: true });
            // Вернулся ТОТ ЖЕ объект — в исходнике APP1 не было. Это не сбой:
            // снимки из мессенджеров и скриншоты приходят без метаданных.
            if (withExif && withExif !== blob) {
                say('feature_use', { f: 'exif_carried', addedB: withExif.size - blob.size });
                return withExif;
            }
            say('feature_use', { f: 'exif_absent_in_source' });
        } catch (e) {
            // Отказ переноса НЕ должен стоить человеку результата: отдаём файл
            // без метаданных и говорим об этом телеметрии.
            say('tool_diagnostic', { issue: 'exif_carry_failed', msg: String(e && e.message).slice(0, 90) });
        }
        return blob;
    }

    window.FRExifCarry = { carry: carry };
})();
